import React, { useEffect, useState } from 'react';
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSession } from "../../utils/session";
import { VIEW_IDS } from "../../constants/ViewIds";


const KPISubHeader = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  const reduxViewIds = useSelector((state) => state.user.viewIds);
  const [viewIds, setViewIds] = useState(reduxViewIds || []);

  const [executionDate, setExecutionDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (reduxViewIds?.length) {
      setViewIds(reduxViewIds);
    } else {
      const session = getSession();
      if (session?.viewIds?.length) setViewIds(session.viewIds);
    }
  }, [reduxViewIds]);

  const canView = (viewId) => viewIds.includes(viewId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://api-project360-insights.haqdarshak.com/api/last-execution-date/');
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setExecutionDate(data.last_execution_date);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch execution date:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString.replace(' AM', ''));
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  const subNavItems = [
    { path: "/Project-Level", label: "Project Level", viewId: VIEW_IDS.PROJECT_LEVEL },
    { path: "/Project-Scheme-Diversity", label: "Scheme Diversity", viewId: VIEW_IDS.SCHEME_DIVERSITY },
    { path: "/Project-KPI-Comparison", label: "Comparison", viewId: VIEW_IDS.COMPARISON }
  ];

  const visibleSubNavItems = subNavItems.filter(item => canView(item.viewId));

  if (visibleSubNavItems.length === 0) return null;

  return (
    <div className={`fixed top-[64px] left-0 right-0 z-40 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' 
        : 'bg-gradient-to-r from-gray-50 to-white border-b border-gray-100'
    }`}>
      <div className="px-4 sm:px-6 ">
        <div className="flex flex-col sm:flex-row justify-between items-center py-2 sm:py-3">
          {/* Navigation Tabs */}
          <div className="flex ml-[40vw] items-center space-x-1 bg-gray-100/50 rounded-lg p-1">
            {visibleSubNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
                  location.pathname === item.path
                    ? "bg-white text-indigo-600 shadow-sm ring-1 ring-indigo-200"
                    : "text-gray-600 hover:text-indigo-600 hover:bg-white/50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Last Update Badge */}
          <div className="mt-2 sm:mt-0">
            {isLoading ? (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 rounded-full">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-500 border-t-transparent"></div>
                <span className="text-xs text-gray-600">Updating...</span>
              </div>
            ) : error ? (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-red-50 rounded-full">
                <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs text-red-600">Update failed</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full border border-green-100">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                  </div>
                  <svg className="w-3.5 h-3.5 text-green-600 relative" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-xs text-gray-700">
                  Last updated: <span className="font-semibold text-gray-900">{formatDate(executionDate)}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPISubHeader;